import { cls } from '@/ui/cls'
import type { Lead } from '@/shared/types'
import { Card, Eyebrow } from '@/ui'
import { stageLabel } from '@/shared/pipelineColors'
import {
  resolveEnrichment,
  companyHealth,
  type FinancialYear,
  type GroupCompany,
} from './mockEnrichment'

// Company intel — financials, koncernstruktur and workplaces. Fed by local mock
// data (../data/mockEnrichment); swap enrichmentFor() for ingested leadgen data
// once the ingest contract carries it. Lives in its own "Company" tab; a compact
// CompanyFactsStrip goes on the Handover glance view.

const fmtSEK = (v: number) => `${(v / 1e6).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} MSEK`
const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(0)}%`

type Tone = 'success' | 'danger' | 'neutral'
function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  const t = tone === 'success' ? 'bg-[var(--color-success)]/10 text-[var(--color-success)] border-[var(--color-success)]/30'
    : tone === 'danger' ? 'bg-[var(--color-danger)]/10 text-[var(--color-danger)] border-[var(--color-danger)]/30'
    : 'bg-ink-50 text-ink-700 border-ink-200'
  return <span className={cls('inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border', t)}>{children}</span>
}

function StageBadge({ stage }: { stage: string }) {
  return <span className="text-[10px] px-1.5 py-px rounded-full bg-brand-soft text-ink-700 border border-brand-deep/40 whitespace-nowrap">{stageLabel(stage)}</span>
}

function Stat({ label, value, sub, danger }: { label: string; value: string; sub?: string; danger?: boolean }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-ink-500">{label}</div>
      <div className={cls('text-[15px] font-display font-semibold tabular-nums', danger ? 'text-[var(--color-danger)]' : 'text-ink-1000')}>{value}</div>
      {sub && <div className={cls('text-[11px] tabular-nums', danger ? 'text-[var(--color-danger)]' : 'text-ink-500')}>{sub}</div>}
    </div>
  )
}

// Revenue trend — area + line, oldest→newest, last value dotted.
function TrendChart({ fin }: { fin: FinancialYear[] }) {
  const pts = [...fin].reverse()
  const vals = pts.map(p => p.revenue)
  const max = Math.max(...vals), min = Math.min(...vals)
  const range = max - min || 1
  const W = 100, H = 38, pad = 3
  const x = (i: number) => pad + (i / Math.max(pts.length - 1, 1)) * (W - pad * 2)
  const y = (v: number) => H - pad - ((v - min) / range) * (H - pad * 2)
  const line = pts.map((p, i) => `${x(i)},${y(p.revenue)}`).join(' ')
  const area = `${x(0)},${H - pad} ${line} ${x(pts.length - 1)},${H - pad}`
  const last = pts[pts.length - 1]
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-20" aria-hidden="true">
        <polygon points={area} className="fill-brand-soft" />
        <polyline points={line} fill="none" className="stroke-brand-deep" strokeWidth="1.2" strokeLinejoin="round" />
        <circle cx={x(pts.length - 1)} cy={y(last.revenue)} r="1.8" className="fill-brand-deep" />
      </svg>
      <div className="flex justify-between text-[10px] text-ink-500 tabular-nums mt-0.5">
        <span>{pts[0].year}</span><span>{last.year}</span>
      </div>
    </div>
  )
}

function GroupNode({ role, name, orgNo, revenue, inPipeline, stage, current, depth }: {
  role: string; name: string; orgNo?: string; revenue?: number; inPipeline?: boolean; stage?: string; current?: boolean; depth: number
}) {
  return (
    <div className="flex items-baseline gap-2 py-1 text-[13px]" style={{ paddingLeft: depth * 16 }}>
      <span className="text-ink-300 shrink-0">{depth > 0 ? '└' : ''}</span>
      <span className="min-w-0">
        <span className={cls(current ? 'font-semibold text-ink-1000' : 'text-ink-1000')}>{name}</span>
        {current && <span className="text-ink-500 text-xs ml-1.5">(det här företaget)</span>}
        {inPipeline && stage && <span className="ml-1.5 align-middle"><StageBadge stage={stage} /></span>}
        <span className="block text-[11px] text-ink-500">
          {role}{orgNo ? ` · ${orgNo}` : ''}{revenue != null ? ` · ${fmtSEK(revenue)}` : ''}
        </span>
      </span>
    </div>
  )
}

function EmptySection() {
  return (
    <Card as="section">
      <Eyebrow as="h4" size="sm" gap="md" className="flex items-center gap-2">Företagsinformation</Eyebrow>
      <p className="text-[13px] text-ink-500 m-0">Ingen data om ekonomi, koncernstruktur eller arbetsställen för det här företaget än.</p>
    </Card>
  )
}

export default function CompanyIntel({ lead }: { lead: Lead }) {
  const data = resolveEnrichment(lead)
  if (!data) {
    return <div className="p-6 max-w-[920px] mx-auto"><EmptySection /></div>
  }

  const fin = data.financials
  const latest = fin[0]
  const prev = fin[1]
  const yoy = prev && prev.revenue ? (latest.revenue - prev.revenue) / prev.revenue : null
  const margin = latest.revenue ? latest.result / latest.revenue : null
  const health = companyHealth(fin)
  const g = data.group

  const members: GroupCompany[] = g ? [g.parent, ...g.subsidiaries].filter(Boolean) as GroupCompany[] : []
  const inPipelineCount = members.filter(m => m.inPipeline).length + 1 // +1 = this company
  const totalMembers = members.length + 1
  const combinedRevenue = members.reduce((a, m) => a + m.revenue, 0) + latest.revenue

  let depth = 0
  const owner = g?.ultimateOwner
  const parent = g?.parent
  const ownerDepth = owner ? depth++ : -1
  const parentDepth = parent ? depth++ : -1
  const thisDepth = depth
  const subDepth = depth + 1

  return (
    <div className="p-6 max-w-[920px] mx-auto flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={health.growth === 'declining' ? 'danger' : health.growth === 'growing' ? 'success' : 'neutral'}>
          {health.growth === 'growing' ? '↑ Växande' : health.growth === 'declining' ? '↓ Minskande' : '→ Stabil'}
          <span className="tabular-nums opacity-80">{fmtPct(health.cagr)}/år</span>
        </Pill>
        <Pill tone={health.profitable ? 'success' : 'danger'}>{health.profitable ? 'Lönsam' : 'Olönsam'}</Pill>
        {health.risk && <Pill tone="danger">⚠ Bevaka</Pill>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <Card as="section">
          <Eyebrow as="h4" size="sm" gap="md" className="flex items-center gap-2">Ekonomi</Eyebrow>
          <div className="flex flex-wrap gap-x-8 gap-y-2 mb-3">
            <Stat label="Omsättning" value={fmtSEK(latest.revenue)} sub={yoy != null ? `${fmtPct(yoy)} å/å` : undefined} danger={yoy != null && yoy < 0} />
            <Stat label="Resultat" value={fmtSEK(latest.result)} danger={latest.result < 0} />
            <Stat label="Marginal" value={margin != null ? fmtPct(margin) : '—'} danger={margin != null && margin < 0} />
            <Stat label="Anställda" value={String(latest.employees)} />
          </div>
          <TrendChart fin={fin} />
          <table className="w-full text-[12px] mt-3">
            <thead>
              <tr className="text-ink-500 text-left">
                <th className="font-medium py-1">År</th>
                <th className="font-medium py-1 text-right">Omsättning</th>
                <th className="font-medium py-1 text-right">Resultat</th>
                <th className="font-medium py-1 text-right">Anst.</th>
              </tr>
            </thead>
            <tbody>
              {fin.map(f => (
                <tr key={f.year} className="border-t border-ink-100">
                  <td className="py-1 tabular-nums text-ink-700">{f.year}</td>
                  <td className="py-1 text-right tabular-nums text-ink-1000">{fmtSEK(f.revenue)}</td>
                  <td className={cls('py-1 text-right tabular-nums', f.result < 0 ? 'text-[var(--color-danger)]' : 'text-ink-1000')}>{fmtSEK(f.result)}</td>
                  <td className="py-1 text-right tabular-nums text-ink-700">{f.employees}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <div className="flex flex-col gap-5">
          {g && (
            <Card as="section">
              <Eyebrow as="h4" size="sm" gap="md" className="flex items-center gap-2">Koncernstruktur</Eyebrow>
              <div className="mb-2 text-[13px] text-ink-700 bg-ink-50 border border-ink-200 rounded px-3 py-2">
                <strong className="text-ink-1000">{inPipelineCount}</strong> av {totalMembers} koncernbolag i din pipeline
                <span className="text-ink-300"> · </span>
                sammanlagd omsättning <strong className="text-ink-1000 tabular-nums">{fmtSEK(combinedRevenue)}</strong>
              </div>
              {owner && <GroupNode role="Yttersta ägare" name={owner.name} orgNo={owner.orgNo} revenue={owner.revenue} depth={ownerDepth} />}
              {parent && <GroupNode role="Moderbolag" name={parent.name} orgNo={parent.orgNo} revenue={parent.revenue} inPipeline={parent.inPipeline} stage={parent.stage} depth={parentDepth} />}
              <GroupNode role="" name={lead.company} current depth={thisDepth} />
              {g.subsidiaries.map(s => (
                <GroupNode key={s.orgNo} role="Dotterbolag" name={s.name} orgNo={s.orgNo} revenue={s.revenue} inPipeline={s.inPipeline} stage={s.stage} depth={subDepth} />
              ))}
            </Card>
          )}

          {data.offices.length > 0 && (
            <Card as="section">
              <Eyebrow as="h4" size="sm" gap="md" className="flex items-center gap-2">Kontor <span className="text-ink-500 normal-case tracking-normal font-normal">({data.offices.length} arbetsställen)</span></Eyebrow>
              <ul className="flex flex-col gap-1">
                {data.offices.map((o, i) => (
                  <li key={i} className="flex items-baseline justify-between text-[13px]">
                    <span className="text-ink-1000">
                      {o.city}
                      {o.isHq && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-ink-500 border border-ink-200 rounded px-1">HK</span>}
                    </span>
                    <span className="tabular-nums text-ink-500">{o.employees} anst.</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

// Compact headline strip for the HandoverCard glance view. Self-hides when the
// company has no data.
export function CompanyFactsStrip({ lead }: { lead: Lead }) {
  const data = resolveEnrichment(lead)
  if (!data) return null
  const latest = data.financials[0]
  const prev = data.financials[1]
  const yoy = prev && prev.revenue ? (latest.revenue - prev.revenue) / prev.revenue : null
  const health = companyHealth(data.financials)
  const g = data.group
  const groupLabel = g?.parent
    ? `Del av ${g.parent.name}`
    : g && g.subsidiaries.length
      ? `${g.subsidiaries.length} dotterbolag`
      : null

  // Geographic footprint — HQ first, then biggest sites. Only worth showing for
  // multi-site companies (a single HQ is already the lead's location).
  const offices = [...data.offices].sort((a, b) => Number(b.isHq) - Number(a.isHq) || b.employees - a.employees)
  const topOffices = offices.slice(0, 4)
  const extraOffices = offices.length - topOffices.length

  return (
    <section className="border-b border-ink-200 px-7 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-ink-700">
        <Eyebrow as="span" size="sm" weight="medium" gap="none">Företag</Eyebrow>
        <span>
          <span className="text-ink-1000 font-semibold tabular-nums">{fmtSEK(latest.revenue)}</span>
          {yoy != null && (
            <span className={cls('ml-1.5 text-[11px] tabular-nums', yoy < 0 ? 'text-[var(--color-danger)]' : 'text-[var(--color-success)]')}>{fmtPct(yoy)} å/å</span>
          )}
        </span>
        <span className="text-ink-300">·</span>
        <span><span className="tabular-nums text-ink-1000 font-medium">{latest.employees}</span> anställda</span>
        {groupLabel && (<><span className="text-ink-300">·</span><span>{groupLabel}</span></>)}
        {health.risk && <Pill tone="danger">⚠ Bevaka</Pill>}
      </div>

      {offices.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          <Eyebrow as="span" size="sm" weight="medium" gap="none" className="mr-0.5">
            {offices.length} arbetsställen
          </Eyebrow>
          {topOffices.map((o, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-ink-200 bg-ink-50 text-ink-700">
              {o.city}
              {o.isHq
                ? <span className="text-[9px] uppercase tracking-wide text-ink-500">HK</span>
                : <span className="tabular-nums text-ink-500">{o.employees}</span>}
            </span>
          ))}
          {extraOffices > 0 && <span className="text-[11px] text-ink-500">+{extraOffices} till</span>}
        </div>
      )}
    </section>
  )
}
